/**
 * Igbo catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade Standard Igbo (with diacritics) at a mathematician's register.
 * **Warrant → «Nkwado»** (support/justification — flagged). Scientific loanwords are used where
 * Igbo standardly borrows (logic → Lọjik, modal, heuristic, geometry, postulate). W/M grades
 * stay Latin (`::numbering-system/latn`). Igbo has NO grammatical plural — the only CLDR
 * cardinal category is `other`, so `frameActive` declares just `other` (plus an explicit `=0`).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const IG_CATALOG = {
    locale: 'ig',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Nyocha',
        [P2.sectionClaims]: 'Nkwupụta',
        [P2.sectionIntegrity]: 'Izuoke',
        [P2.axisWarrantLabel]: 'Nkwado',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Nke nnwale',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Nkọwa} interp {Nkọwapụta} testable {Enwere ike ịnwale} tested {Anwalere} refuted {Ajụrụ ajụ} program {Mmemme nyocha} philos {Nke nkà ihe ọmụma} other {Amaghị}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ọkwa ike {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Enwere ike inyocha',
        [P2.stateRefuted]: 'Ajụrụ ajụ',
        [P2.stateOutside]: 'Na mpụga oghere',
        [P2.verdictConflict]: 'Na-emegide {constraints}',
        [P2.verdictOutside]: 'Chọrọ {missing}',
        [P2.lossRegisterPlain]: "Emere ka ọ dị mfe n'asụsụ nkịtị — e hapụrụ ụfọdụ izi ezi.",
        [P2.lossRegisterKid]: 'Emere ka ọ dị mfe maka ụmụaka na-agụ — e hapụrụ ọtụtụ nkọwa.',
        [P2.lossProfileDetail]: 'A chịkọtara profaịlụ zuru ezu nke Nkwado / Nke nnwale / Modal ebe a.',
        [P2.lossConflictDetail]: 'E belatara nkọwa esemokwu na nlele a.',
        [P2.laneVerified]: 'Enyochara',
        [P2.laneUnverified]: 'Enyochaghị',
        [P2.laneConflict]: 'Egosipụtara esemokwu',
        [P2.constraintLogic]: 'Lọjik',
        [P2.constraintLightBends]: "Ìhè na-agba n'ọhịa ndọda",
        [P2.constraintParallelPostulate]: 'Postulate nke ahịrị yiri',
        [P2.claimScalarGravity]: 'Echiche skala nke ndọda',
        [P2.claimNonEuclidean]: 'Geometry na-abụghị nke Euclid',
        [P2.claimSubstrateSr]: 'Nkọwa substrate nke njikọ pụrụ iche',
        [P2.edgeExtObservation]: 'Nleba anya mpụga',
        [P2.edgeResourceSample]: 'Akụrụngwa mgbako e kekọrịtara',
        [P2.edgeHeuristicSample]: 'Nduzi heuristic',
        [P2.edgeSharedSample]: 'Ntọala ekekọrịtara',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Mmachi',
        [P2.sectionEdges]: 'Akụkụ',
        [P2.sectionFrame]: 'Freemu',
        [P2.sectionExposure]: 'Ngosipụta',
        [P2.statusAdoptedFormal]: "Anabatara (n'usoro)",
        [P2.statusAdoptedInterpretive]: 'Anabatara (nkọwa)',
        [P2.statusSuspended]: 'Akwụsịtụrụ',
        [P2.edgeEffectConstraint]: 'Na-eke mmachi (nwere ike ịjụ)',
        [P2.edgeEffectResource]: 'Akụrụngwa — enweghị mmetụta na mkpebi',
        [P2.edgeEffectHeuristic]: 'Heuristic — enweghị mmetụta na mkpebi',
        [P2.edgeEffectShared]: 'Ntọala ekekọrịtara — enweghị mmetụta na mkpebi',
        [P2.frameActive]: '{count, plural, =0 {Enweghị mmachi na-arụ ọrụ} other {# mmachi na-arụ ọrụ}}',
        [P2.exposureNone]: 'Enweghị ndabere nkọwa',
        [P2.exposureDep]: 'Dabere na {id}',
        [P2.laneTrackMachine]: 'Igwe',
        [P2.laneTrackExpert]: 'Ọkachamara',
        [P2.laneTrackCommunity]: 'Obodo',
        [P2.expertAudited]: 'Enyochara',
        [P2.expertInProgress]: "Na-aga n'ihu",
        [P2.expertNotReviewed]: 'Enyochaghị',
        [P2.communityFlags]: 'Enwere akara',
        [P2.communityNone]: 'Enweghị akara',
        [P2.controlChallenge]: 'Gọnahụ',
        [P2.controlActivate]: 'Kwado',
        [P2.controlMakeInterpretive]: 'Mee ka ọ bụrụ nkọwa',
        [P2.controlImportEdge]: 'Bubata akụkụ',
        [P2.controlPromote]: 'Kwalite',
        [P2.controlUndo]: 'Kagbuo',
        [P2.controlRedo]: 'Megharịa',
        [P2.controlRegister]: 'Debanye aha',
        [P2.promoteRejected]: 'Ajụrụ nkwalite',
        [P2.promoteAccepted]: 'Anabatara nkwalite',
        [P2.promoteReasonRefutationTerminal]: 'Ihe akaebe enweghị ike ịgwọ njụ.',
        [P2.promoteReasonKeystone]: 'Nkwalite dị mkpa chọrọ nkwado nnwale.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Edozighị ụdị nnwale.',
        [P2.promoteReasonModalNotFixed]: 'Edozighị ọkwa modal.',
        [P2.promoteReasonWarrantNotImproved]: 'Emeliteghị ọkwa nkwado.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ig.js.map